import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { C, PageHeader, GlassCard, Button, Badge } from "@coremonit/design-system";
import {
  LayoutGrid, Activity, Terminal, GitBranch, Map, Bell, ContainerIcon, BookOpen, Maximize2,
} from "lucide-react";
import type { WorkspacePanel } from "@coremonit/types";

const PRESETS: Array<{ id: string; name: string; panels: WorkspacePanel[] }> = [
  {
    id: "logs-traces",
    name: "Logs + Traces",
    panels: [
      { id: "p1", type: "logs", title: "Log Explorer" },
      { id: "p2", type: "traces", title: "Distributed Tracing" },
    ],
  },
  {
    id: "metrics-deploy",
    name: "Metrics + Deployments",
    panels: [
      { id: "p1", type: "metrics", title: "Live Metrics" },
      { id: "p2", type: "deployments", title: "Deployment Timeline" },
    ],
  },
  {
    id: "topo-alerts",
    name: "Topology + Alerts",
    panels: [
      { id: "p1", type: "topology", title: "Cluster Topology" },
      { id: "p2", type: "alerts", title: "Active Alerts" },
    ],
  },
  {
    id: "pod-logs",
    name: "Pod + Container Logs",
    panels: [
      { id: "p1", type: "pod", title: "Container Explorer" },
      { id: "p2", type: "logs", title: "Container Logs" },
    ],
  },
];

const PANEL_PATH: Record<WorkspacePanel["type"], (cluster?: string | null) => string> = {
  logs: () => "/logs",
  traces: () => "/traces",
  metrics: () => "/metrics",
  deployments: () => "/deployments",
  topology: (cluster) => cluster ? `/clusters/${cluster}/topology` : "/map",
  alerts: () => "/alerts",
  pod: () => "/containers",
  runbook: () => "/rca",
};

const PANEL_ICON: Record<WorkspacePanel["type"], typeof Activity> = {
  logs: Terminal,
  traces: Activity,
  metrics: Activity,
  deployments: GitBranch,
  topology: Map,
  alerts: Bell,
  pod: ContainerIcon,
  runbook: BookOpen,
};

export function OperationsWorkspacePage() {
  const [params] = useSearchParams();
  const cluster = params.get("cluster");
  const nav = useNavigate();
  const [presetId, setPresetId] = useState(PRESETS[cluster ? 2 : 0].id);
  const [layout, setLayout] = useState<"row" | "col">("row");

  const preset = useMemo(() => PRESETS.find((p) => p.id === presetId) ?? PRESETS[0], [presetId]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "calc(100vh - 120px)" }}>
      <PageHeader
        icon={<LayoutGrid size={16} />}
        title="Operations Workspace"
        subtitle="Multi-panel investigation — correlate logs, traces, metrics, and deployments side by side."
        badge={cluster ? <Badge color="blue">{cluster}</Badge> : undefined}
        actions={
          <>
            <Button variant="secondary" icon={<Maximize2 size={12} />} onClick={() => setLayout((l) => l === "row" ? "col" : "row")}>
              {layout === "row" ? "Stack vertical" : "Split horizontal"}
            </Button>
          </>
        }
      />

      <GlassCard style={{ padding: 10 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPresetId(p.id)}
              style={{
                padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer",
                background: presetId === p.id ? C.blueDim : C.surfaceHigh,
                border: `1px solid ${presetId === p.id ? C.borderBright : C.border}`,
                color: presetId === p.id ? C.blue : C.textSub,
              }}
            >
              {p.name}
            </button>
          ))}
        </div>
      </GlassCard>

      <div
        style={{
          flex: 1, display: "grid", gap: 10, minHeight: 0,
          gridTemplateColumns: layout === "row" ? "1fr 1fr" : "1fr",
          gridTemplateRows: layout === "col" ? "1fr 1fr" : "1fr",
        }}
      >
        {preset.panels.map((panel) => {
          const Icon = PANEL_ICON[panel.type];
          const path = PANEL_PATH[panel.type](cluster);
          return (
            <GlassCard key={panel.id} style={{ padding: 0, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px", borderBottom: `1px solid ${C.border}`, background: C.surfaceHigh,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.text, fontSize: 12, fontWeight: 700 }}>
                  <Icon size={13} color={C.blue} /> {panel.title}
                </div>
                <Button variant="ghost" size="xs" onClick={() => nav(path)}>Open full view</Button>
              </div>
              <div style={{ flex: 1, padding: 14, overflow: "auto", color: C.textMuted, fontSize: 11 }}>
                <PanelPreview type={panel.type} cluster={cluster} onNavigate={() => nav(path)} />
              </div>
            </GlassCard>
          );
        })}
      </div>
    </div>
  );
}

function PanelPreview({ type, cluster, onNavigate }: { type: WorkspacePanel["type"]; cluster: string | null; onNavigate: () => void }) {
  const lines: Record<WorkspacePanel["type"], string[]> = {
    logs: ["[12:04:01] ERROR checkout-service — payment timeout after 30s", "[12:04:02] WARN  gateway — retry attempt 2/3", "[12:04:05] INFO  payment-service — stripe webhook received"],
    traces: ["trace_a8f2 · checkout → payment · 842ms · error", "trace_b91c · gateway → inventory · 45ms · ok", "trace_c3d4 · user-service → redis · 12ms · ok"],
    metrics: ["CPU 68% · Memory 4.2GB · RPS 1,240", "P95 latency 142ms · Error rate 0.8%", "Queue depth 12 · DB connections 45/100"],
    deployments: ["v1.4.2 · rolling · 3m ago · success", "v1.4.1 · canary · 2d ago · success", "v1.4.0 · blue/green · 4d ago · rolled back"],
    topology: cluster ? [`Cluster ${cluster} — 8 services, 3 namespaces`, "ingress → checkout → payment → stripe", "Click to open interactive map with zoom & pan"] : ["Service dependency graph — gateway at center", "12 nodes · 18 edges · 2 degraded paths"],
    alerts: ["CRITICAL · payment-service error rate > 5%", "WARNING · prod-eks-01 CPU > 85%", "INFO · deployment v1.4.2 completed"],
    pod: ["pod/checkout-7f8b9c · running · 2 containers", "CPU 340m · Memory 512Mi · 0 restarts", "Sidecar: envoy-proxy · Init: migration-job"],
    runbook: ["1. Check recent deployment on payment-service", "2. Inspect Stripe API latency in traces", "3. Scale replicas if queue depth > 50"],
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {lines[type].map((line) => (
        <div key={line} style={{ padding: "8px 10px", background: C.surfaceHigh, borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: type === "logs" ? "ui-monospace, monospace" : undefined, color: C.textSub }}>
          {line}
        </div>
      ))}
      <button
        onClick={onNavigate}
        style={{ marginTop: 8, padding: "8px 12px", background: C.blueDim, border: `1px solid ${C.borderBright}`, borderRadius: 8, color: C.blue, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
      >
        Open in full module →
      </button>
    </div>
  );
}
